import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { agentMerchantApi } from '../services/api';
import ChatBox, { mergeIncoming } from '../components/ChatBox';

// 预约日历。
//
// 为什么要有它：一家店同时约七八个博主时（运营说确实有商家一次买 10-12 个），
// 「谁哪天来、去哪家店」靠翻聊天记录永远看不出来。加聊天筛选也解决不了 ——
// 聊天是按时间走的，回答不了「这个月排成什么样」。
//
// 手机上不画月历网格：一行七格在小屏上点不准，而且商家真正要的是
// 「最近这几天谁来」，不是「回看 3 月 5 号」。所以按天分组、只列有预约的日子。
// 商家只看三个节点：已约时间 → 已到店 → 已发布。
// 中间还有交稿、审核、待发布几步，那是我们跟博主之间的事 ——
// 让老板看见他就会问「审多久」「我能不能也看一眼」，
// 而我们的设计是商家不参与审稿（逐篇给老板看会变成没完没了的来回）。
function merchantStage(d) {
  if (d.publishedAt) return '已发布';
  if (d.arrived) return '已到店';
  if (d.visitAt) return '已约时间';
  return '';
}

function Schedule({ deals, onPick }) {
  const upcoming = deals
    .filter((d) => d.visitAt && !['declined', 'expired'].includes(d.status))
    .sort((a, b) => new Date(a.visitAt) - new Date(b.visitAt));
  if (!upcoming.length) return null;

  const byDay = {};
  upcoming.forEach((d) => {
    const k = new Date(d.visitAt).toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric', weekday: 'short' });
    (byDay[k] = byDay[k] || []).push(d);
  });

  const today = new Date().toDateString();
  return (
    <div style={s.schWrap}>
      <div style={s.schTitle}>预约日历</div>
      {Object.entries(byDay).map(([day, list]) => (
        <div key={day} style={s.schDay}>
          <div style={s.schDayHead}>
            {day}
            {new Date(list[0].visitAt).toDateString() === today && <span style={s.schToday}>今天</span>}
            <span style={s.schCount}>{list.length} 位</span>
          </div>
          {list.map((d) => (
            <div key={d._id} style={s.schRow} onClick={() => onPick(d)}>
              <span style={s.schTime}>
                {new Date(d.visitAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })}
              </span>
              <span style={s.schWho}>{d.blogger?.name || '博主'}</span>
              <span style={s.schMeta}>
                {d.peopleCount} 人{d.storeAddress ? ` · ${d.storeAddress}` : ''}
              </span>
              <span style={s.schState}>{merchantStage(d)}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function QuickRow({ label, unit, v, on, placeholder }) {
  return (
    <div style={s.quickRow}>
      <span style={s.quickLabel}>{label}</span>
      <input style={s.quickNum} inputMode="numeric" value={v} placeholder={placeholder}
        onChange={(e) => on(e.target.value.replace(/[^0-9]/g, ''))} />
      <span style={s.quickUnit}>{unit}</span>
    </div>
  );
}

const WD = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
const DEAL_LABEL = {
  matched: '待邀请', invited: '已邀请', negotiating: '沟通中',
  agreed: '待你确认', declined: '已婉拒', expired: '无回应', committed: '已安排',
};

const fmtWhen = (d) => new Date(d).toLocaleString('zh-CN', {
  month: 'long', day: 'numeric', weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
});

// 商家端：一条对话搞定一切。条件不用填表 —— 跟助理说，它边聊边记。
// 表单只作为「校对/微调」入口藏在条件卡片后面，不是主路径。
export default function MerchantAgent() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await agentMerchantApi.get(token);
      setData(d);
      setMessages(await agentMerchantApi.messages(token));
    } catch (e) {
      setMsg(e?.response?.data?.error || '链接无效');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const send = async (text) => {
    setSending(true);
    setMessages((p) => [...p, { sender: 'merchant', text, _id: 'tmp' + Date.now() }]);
    try {
      await agentMerchantApi.say(token, text);
      await load();                       // 条件可能被助理改了，整体刷新
    } finally { setSending(false); }
  };

  const [rating, setRating] = useState(null);   // 正在打分的撮合单

  const submitRating = async () => {
    await agentMerchantApi.rate(token, rating._id, {
      onTime: rating.onTime, content: rating.content, cooperation: rating.cooperation, comment: rating.comment,
    });
    setRating(null);
    load();
  };

  const [quick, setQuick] = useState({ mealValue: '', peopleAllowed: '', neededCount: '', availabilityText: '' });
  const [quickDone, setQuickDone] = useState(false);
  const [pick, setPick] = useState(null);          // 点开的那条预约
  const [pickMsgs, setPickMsgs] = useState(null);

  const loadPickMsgs = async (dealId) => {
    setPickMsgs(null);
    try { setPickMsgs(await agentMerchantApi.dealMessages(token, dealId)); }
    catch { setPickMsgs([]); }
  };

  // 快速填其实就是「替老板把这句话打出来」——
  // 存完条件再以他的身份说一句，助理接着往下问缺的那几项，对话是连贯的
  const submitQuick = async () => {
    const q = quick;
    const said = [
      q.mealValue ? `套餐 $${q.mealValue}` : '',
      q.peopleAllowed ? `到店 ${q.peopleAllowed} 人` : '',
      q.neededCount ? `这轮招 ${q.neededCount} 位` : '',
      q.availabilityText ? `可接待 ${q.availabilityText}` : '',
    ].filter(Boolean).join('，');
    if (!said) { setQuickDone(true); return; }
    setQuickDone(true);
    await send(said);
  };

  const markArrived = async (dealId) => {
    if (!window.confirm('确认这位博主已经到店？点了之后会提醒他尽快交稿。')) return;
    try { await agentMerchantApi.arrived(token, dealId); await load(); }
    catch (e) { setMsg(e?.response?.data?.error || '没记上，稍后再试'); }
  };

  const approve = async (dealId, approved) => {
    await agentMerchantApi.approve(token, dealId, approved);
    load();
  };

  if (!data) return <div style={s.center}>{msg || '加载中…'}</div>;

  const t = data.terms;
  // 快捷短语跟着「还缺什么」变，别让商家自己想说什么
  const quickReplies = [];
  if (!t?.mealValue) quickReplies.push('套餐给到 $120', '套餐给到 $80');
  if (!t?.peopleAllowed) quickReplies.push('一次来 2 个人');
  if (!t?.neededCount) quickReplies.push('先招 3 个博主');
  if (!(t?.availability || []).length) quickReplies.push('周三周五晚上能接', '周末中午能接');
  if (!t?.postCount) quickReplies.push('发 1 篇小红书就行');
  if (!t?.deadlineDays) quickReplies.push('7 天内发出来');
  if (!quickReplies.length) quickReplies.push('现在谈得怎么样了', '这周还能加人吗', '我想改一下条件');
  const pending = (data.deals || []).filter((d) => d.status === 'agreed' && !d.merchantApproved);
  const settled = (data.deals || []).filter((d) => d.merchantApproved || d.status === 'committed');
  // 时间已经定下来的都算「即将到店」（含等商家确认的），按到店时间排
  // 只要时间定下来了就让商家看到（还在谈的标注「暂定」），
  // 否则博主那边已经约好了、商家这边还一无所知
  const toRate = (data.deals || []).filter((d) => d.postUrl && !d.rated);
  const upcoming = (data.deals || [])
    .filter((d) => d.visitAt && ['agreed', 'committed', 'negotiating'].includes(d.status))
    .sort((a, b) => new Date(a.visitAt) - new Date(b.visitAt));
  const talking = (data.deals || []).filter((d) => ['invited', 'negotiating'].includes(d.status));
  // 已结束：黄了的、和已经发完帖的
  const ended = (data.deals || []).filter((d) => d.endedReason || d.publishedAt);

  return (
    <div style={s.page}>
      <div style={s.top}>
        <div style={s.shop}>{data.merchant.name}</div>
        <div style={s.addr}>{data.merchant.address}</div>
      </div>

      {/* 第一次进来给一张快速填的卡片。
          为什么不是常驻表单：老板要找、要填、容易填错，而且他买的就是省事。
          为什么也不是纯聊天：最基本的四项打字说太慢，能点就点。
          所以只在「条件还什么都没定」时出现一次，填完或跳过就再也不出现，
          剩下的（必拍菜品、禁用词、现金授权…）交给助理聊的时候补。
          填过的助理不会再问一遍 —— 它的上下文里有「已经知道的，绝对不要再问」。*/}
      {!t?.mealValue && !quickDone && (
        <div style={s.quickCard}>
          <div style={s.quickTitle}>先花半分钟说一下基本情况</div>
          <div style={s.quickSub}>能填多少填多少，不想填直接跳过 —— 下面跟助理聊也一样，它会问你</div>
          <QuickRow label="一位博主的套餐价值" unit="澳元" v={quick.mealValue}
            on={(v) => setQuick({ ...quick, mealValue: v })} placeholder="120" />
          <QuickRow label="一位博主到店几个人" unit="含本人" v={quick.peopleAllowed}
            on={(v) => setQuick({ ...quick, peopleAllowed: v })} placeholder="2" />
          <QuickRow label="这一轮想招几位" unit="位" v={quick.neededCount}
            on={(v) => setQuick({ ...quick, neededCount: v })} placeholder="3" />
          <div style={s.quickLabel}>哪几天什么时段方便接待</div>
          <input style={s.quickText} value={quick.availabilityText} placeholder="比如：周三周五晚上、周末中午"
            onChange={(e) => setQuick({ ...quick, availabilityText: e.target.value })} />
          <div style={s.quickBtns}>
            <button style={s.quickSkip} onClick={() => setQuickDone(true)}>先跳过，直接聊</button>
            <button style={s.quickGo} onClick={submitQuick}>就这些，开始</button>
          </div>
        </div>
      )}

      {/* 条件摘要。只读 —— 不给填表入口。
          老板要的是省事：跟助理说一句「套餐改成 150」比让他找到表单、找到那一栏、
          改完再点保存快得多，也不会填错格式。摘要留着是为了让他一眼看出我们记得对不对，
          记错了直接在下面说一句就行。
          （原来点这一条会弹出一张表单，现在整个收起来了） */}
      <div style={s.termsBar}>
        {t ? (
          <span style={s.termsText}>
            套餐 ${t.mealValue || 0} · 到店 {t.peopleAllowed || 2} 人 · 发 {t.postCount || 1} 篇 ·
            {' '}{t.deadlineDays || 7} 天内 · {(t.availability || []).length
              ? t.availability.map((x) => `${WD[x.weekday]}${x.from}-${x.to}`).join('、')
              : '时段未定'}
            <span style={s.termsHint}>　记错了跟助理说一声就行</span>
          </span>
        ) : (
          <span style={s.termsEmpty}>条件还没定 —— 直接在下面告诉助理就行，不用填表</span>
        )}
      </div>

      {(talking.length > 0 || settled.length > 0) && (
        <div style={s.progressBar}>
          {settled.length > 0 && <span style={s.progOk}>已安排 {settled.length}</span>}
          {talking.length > 0 && <span style={s.progGo}>沟通中 {talking.length}</span>}
          {t?.neededCount ? <span style={s.progSub}>目标 {t.neededCount} 人</span> : null}
        </div>
      )}

      {/* 预约日历：一眼看清这几天谁来、几点、几个人、去哪家店。
          点一条进详情，能看到跟这位博主相关的往来 */}
      <Schedule deals={data.deals || []} onPick={(d) => { setPick(d); loadPickMsgs(d._id); }} />

      {/* 即将到店：备餐和接待要用的信息，别让商家去翻聊天记录 */}
      {upcoming.length > 0 && (
        <div style={s.upWrap}>
          <div style={s.upTitle}>即将到店</div>
          {upcoming.map((d) => (
            <div key={d._id} style={s.upCard}>
              <div style={s.upWhen}>{d.visitAt ? fmtWhen(d.visitAt) : '时间待定'}</div>
              <div style={s.upWho}>
                {d.blogger?.name || '博主'}
                <span style={s.upSub}>
                  {d.blogger?.xhsAccount ? ` 小红书 ${d.blogger.xhsAccount}` : ''}
                  {d.blogger?.xhsFollowers ? ` · ${d.blogger.xhsFollowers} 粉` : ''}
                </span>
              </div>
              <div style={s.upMeta}>
                {/* 多门店的商家，店员最需要知道的是「这个人来我这家吗」 */}
                {d.storeAddress ? <span style={s.upStore}>{d.storeAddress}　</span> : null}
                {d.peopleCount} 人到店
                {d.note ? ` · ${d.note}` : ''}
                {d.status === 'negotiating' ? ' · 时间暂定，还在谈'
                  : (!d.merchantApproved ? ' · 等你确认' : '')}
              </div>
              {d.blogger?.xhsUrl && (
                <a style={s.upLink} href={d.blogger.xhsUrl} target="_blank" rel="noreferrer">看他的小红书</a>
              )}
              {/* 店员在店里点这个。到店是个明确的时间点，从这一刻起该催稿了 ——
                  博主那边会立刻收到提醒。原来没有这个入口，到店状态只能靠助理问出来 */}
              {d.arrived ? (
                <div style={s.arrivedTag}>已到店</div>
              ) : (
                <button style={s.arriveBtn} onClick={() => markArrived(d._id)}>他到店了</button>
              )}
            </div>
          ))}
        </div>
      )}

      {talking.filter((d) => !d.visitAt).length > 0 && (
        <div style={s.talkBar}>
          正在谈：{talking.filter((d) => !d.visitAt).map((d) => d.blogger?.name || '博主').join('、')}　谈成了会出现在上面
        </div>
      )}

      {/* 已结束：黄了的单子原来直接从界面上消失，商家事后什么都查不到 ——
          「上次那个说要来又没来的是谁」答不上来。放在最下面，不占地方 */}
      {ended.length > 0 && (
        <div style={s.endedWrap}>
          <div style={s.endedTitle}>已结束（{ended.length}）</div>
          {ended.map((d) => (
            <div key={d._id} style={s.endedRow} onClick={() => { setPick(d); loadPickMsgs(d._id); }}>
              <span style={s.endedWho}>{d.blogger?.name || '博主'}</span>
              <span style={s.endedMeta}>
                {d.visitAt ? fmtWhen(d.visitAt) : '没约上时间'}
                {d.storeAddress ? ` · ${d.storeAddress}` : ''}
                {' · '}{d.publishedAt ? '已发布' : (d.endedReason || '已完成')}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* 一条预约的详情 + 跟这位博主相关的往来（只读）。
          不给回复入口：商家跟助理只有一条对话，在这儿回要么写进主对话等于没分开，
          要么另开一条、老板会分不清跟谁说过什么。要看全部就回主对话翻 */}
      {pick && (
        <div style={s.overlay} onClick={() => setPick(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <p style={s.modalTitle}>{pick.blogger?.name || '博主'}</p>
            <div style={s.pickMeta}>
              {pick.visitAt ? fmtWhen(pick.visitAt) : '时间未定'} · {pick.peopleCount} 人
              {pick.storeAddress ? ` · ${pick.storeAddress}` : ''}
            </div>
            {pick.blogger?.xhsAccount && (
              <div style={s.pickMeta}>小红书 {pick.blogger.xhsAccount}
                {pick.blogger.xhsFollowers ? ` · ${pick.blogger.xhsFollowers} 粉` : ''}</div>
            )}
            {pick.note && <div style={s.pickMeta}>备注：{pick.note}</div>}
            <div style={s.pickState}>{merchantStage(pick) || '还在谈'}</div>

            <div style={s.pickMsgTitle}>跟他相关的消息</div>
            <div style={s.pickMsgs}>
              {!pickMsgs ? <div style={s.pickEmpty}>加载中…</div>
                : !pickMsgs.length ? <div style={s.pickEmpty}>还没有跟这位博主相关的消息</div>
                  : pickMsgs.map((m) => (
                    <div key={m._id} style={m.sender === 'merchant' ? s.pickMine : s.pickTheirs}>
                      {m.text}
                    </div>
                  ))}
            </div>
            <div style={s.modalBtns}>
              <button style={s.cancel} onClick={() => setPick(null)}>知道了</button>
            </div>
          </div>
        </div>
      )}

      {msg && <div style={s.msg} onClick={() => setMsg('')}>{msg}</div>}

      <ChatBox
        conversationId={data.conversationId}
        messages={messages}
        sending={sending}
        onSend={send}
        imAuth={{ role: 'merchant', token }}
        onIncoming={(m) => {
          setMessages((p) => mergeIncoming(p, m));
          // 助理说「已经帮您改好了」的同时，下面的行程卡也得跟着变。
          // 原来只有「进页面/自己发消息/打分/存条件」会刷新，别人改了时间
          // 这边毫无察觉 —— 运营报过：AI 说改了，但商家看到的还是旧时间。
          if (m?.sender === 'agent' || m?.sender === 'ops') load();
        }}
        placeholder="跟助理说，比如「套餐给到120，周五中午能接两个」"
        height={window.innerHeight - (t ? 250 : 230)}
        renderAfter={
          <>
            {pending.map((d) => (
              <div key={d._id} style={s.dealCard}>
                <div style={s.dealTop}>
                  <span style={s.dealName}>{d.blogger?.name || '博主'}</span>
                  {d.blogger?.xhsFollowers > 0 && <span style={s.dealSub}>{d.blogger.xhsFollowers} 粉</span>}
                  <span style={s.dealTag}>{DEAL_LABEL[d.status]}</span>
                </div>
                {d.blogger?.bio && <div style={s.dealBio}>{d.blogger.bio}</div>}
                <div style={s.dealLine}>
                  {d.visitAt ? fmtWhen(d.visitAt) : '时间未定'} · {d.peopleCount} 人
                  {d.note ? ` · ${d.note}` : ''}
                </div>
                <div style={s.dealActions}>
                  <button style={s.okBtn} onClick={() => approve(d._id, true)}>确认接待</button>
                  <button style={s.noBtn} onClick={() => approve(d._id, false)}>先不要</button>
                </div>
              </div>
            ))}
            {toRate.map((d) => (
              <div key={d._id} style={s.rateCard}>
                <div style={s.dealTop}>
                  <span style={s.dealName}>{d.blogger?.name || '博主'}</span>
                  <span style={s.dealTag}>已发布</span>
                </div>
                <div style={s.verifyRow}>
                  {d.verifyCode === 'ok' && <span style={s.vOk}>平台已核实：笔记在线，作者对得上</span>}
                  {d.verifyCode === 'ok_unverified' && <span style={s.vWarn}>笔记在线，但作者没核对上</span>}
                  {d.verifyCode === 'gone' && <span style={s.vBad}>笔记已打不开，可能被删</span>}
                </div>
                <a style={s.upLink} href={d.postUrl} target="_blank" rel="noreferrer">看他发的内容</a>
                <div style={s.dealActions}>
                  <button style={s.okBtn} onClick={() => setRating({ _id: d._id, name: d.blogger?.name, onTime: 5, content: 5, cooperation: 5, comment: '' })}>
                    给他打个分
                  </button>
                </div>
              </div>
            ))}
            {settled.filter((d) => !toRate.some((r) => r._id === d._id)).map((d) => (
              <div key={d._id} style={s.settledCard}>
                已安排 · {d.blogger?.name || '博主'} · {d.visitAt ? fmtWhen(d.visitAt) : '时间未定'} · {d.peopleCount} 人
                {d.rated ? ' · 已评价' : ''}
              </div>
            ))}
          </>
        }
      />

      {rating && (
        <div style={s.overlay} onClick={() => setRating(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <p style={s.modalTitle}>给 {rating.name} 打分</p>
            <p style={s.modalHint}>
              按时交、完成度高的，下次优先推给您。打分只看他把活干得怎么样，
              跟他在帖子里夸不夸店没关系。
            </p>
            {[
              ['onTime', '准时到店', ''],
              // 「内容质量」原来是含糊的 —— 商家可能理解成「写得好不好听」，
              // 那就变成按评价倾向给人打分、而分数又会影响他下次有没有活干。
              // 改成对着客观标准打：字数、图片、话题达不达标。
              ['content', '内容完成度', '字数、图片、话题是否达标'],
              ['cooperation', '配合度', '沟通顺不顺、有没有按约定来'],
            ].map(([k, label, hint]) => (
              <div key={k}>
                <div style={s.label}>{label}</div>
                {hint && <div style={s.hint}>{hint}</div>}
                <div style={s.starRow}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <span key={n} style={n <= rating[k] ? s.starOn : s.starOff}
                      onClick={() => setRating({ ...rating, [k]: n })}>{n}</span>
                  ))}
                </div>
              </div>
            ))}
            <div style={s.label}>想说的（选填）</div>
            <textarea style={s.area} rows={2} value={rating.comment}
              onChange={(e) => setRating({ ...rating, comment: e.target.value })}
              placeholder="比如「按时交了，图拍得清楚」" />
            <div style={s.modalBtns}>
              <button style={s.cancel} onClick={() => setRating(null)}>下次再说</button>
              <button style={s.save} onClick={submitRating}>提交</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


const s = {
  page: { minHeight: '100vh', background: '#f7f7f8', maxWidth: 480, margin: '0 auto' },
  center: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' },
  top: { background: '#fff', padding: '12px 16px', borderBottom: '1px solid #f0f0f0' },
  shop: { fontSize: 17, fontWeight: 700, color: '#111' },
  addr: { fontSize: 12, color: '#9ca3af', marginTop: 2 },
  termsBar: {
    background: '#fff', padding: '9px 16px', borderBottom: '1px solid #f0f0f0',
    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
  },
  termsText: { flex: 1, fontSize: 12, color: '#4b5563', lineHeight: 1.6 },
  termsHint: { fontSize: 11, color: '#9ca3af' },
  // 预约日历
  schWrap: { background: '#fff', margin: '10px 12px', padding: '14px 16px', borderRadius: 12, border: '1px solid #f0f0f0' },
  schTitle: { fontSize: 15, fontWeight: 600, color: '#111', marginBottom: 10 },
  schDay: { marginBottom: 12 },
  schDayHead: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 },
  schToday: { fontSize: 11, color: '#fff', background: '#FA1C51', padding: '1px 7px', borderRadius: 999 },
  schCount: { fontSize: 11, color: '#9ca3af', fontWeight: 400 },
  schRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderTop: '1px solid #f5f5f6', fontSize: 13, cursor: 'pointer' },
  schTime: { color: '#FA1C51', fontWeight: 600, width: 46 },
  schWho: { color: '#111', fontWeight: 500 },
  schMeta: { color: '#9ca3af', fontSize: 12, flex: 1 },
  schState: { color: '#16a34a', fontSize: 12 },
  // 已结束
  endedWrap: { background: '#fff', margin: '10px 12px', padding: '12px 16px', borderRadius: 12, border: '1px solid #f0f0f0' },
  endedTitle: { fontSize: 13, color: '#9ca3af', marginBottom: 8 },
  endedRow: { display: 'flex', alignItems: 'baseline', gap: 8, padding: '6px 0', borderTop: '1px solid #f7f7f8', cursor: 'pointer' },
  endedWho: { fontSize: 13, color: '#374151' },
  endedMeta: { fontSize: 12, color: '#b0b4ba' },
  // 预约详情
  pickMeta: { fontSize: 13, color: '#6b7280', marginTop: 4, lineHeight: 1.7 },
  pickState: { display: 'inline-block', marginTop: 8, fontSize: 12, color: '#FA1C51', background: '#fff1f4', padding: '2px 10px', borderRadius: 999 },
  pickMsgTitle: { fontSize: 12, color: '#9ca3af', margin: '14px 0 6px' },
  pickMsgs: { maxHeight: '40vh', overflowY: 'auto', background: '#f8f8f9', borderRadius: 8, padding: 10 },
  pickEmpty: { fontSize: 12, color: '#9ca3af', padding: '6px 0' },
  pickTheirs: { fontSize: 13, color: '#374151', background: '#fff', padding: '7px 10px', borderRadius: 8, marginBottom: 6, lineHeight: 1.6 },
  pickMine: { fontSize: 13, color: '#fff', background: '#FA1C51', padding: '7px 10px', borderRadius: 8, marginBottom: 6, marginLeft: 30, lineHeight: 1.6 },
  quickCard: { background: '#fff', margin: '10px 12px', padding: '14px 16px', borderRadius: 12, border: '1px solid #f0f0f0' },
  quickTitle: { fontSize: 15, fontWeight: 600, color: '#111' },
  quickSub: { fontSize: 12, color: '#9ca3af', margin: '4px 0 12px', lineHeight: 1.6 },
  quickRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 },
  quickLabel: { fontSize: 13, color: '#374151', flex: 1 },
  quickNum: { width: 72, padding: '7px 10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, textAlign: 'center', outline: 'none' },
  quickUnit: { fontSize: 12, color: '#9ca3af', width: 40 },
  quickText: { width: '100%', padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', margin: '4px 0 12px' },
  quickBtns: { display: 'flex', gap: 10 },
  quickSkip: { flex: 1, padding: '9px 0', background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13 },
  quickGo: { flex: 2, padding: '9px 0', background: '#FA1C51', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600 },
  termsEmpty: { flex: 1, fontSize: 12, color: '#FA1C51', lineHeight: 1.6 },
  termsEdit: { fontSize: 12, color: '#9ca3af', flexShrink: 0 },
  progressBar: { background: '#fff', padding: '6px 16px 10px', display: 'flex', gap: 10, borderBottom: '1px solid #f0f0f0' },
  progOk: { fontSize: 12, color: '#16a34a', fontWeight: 600 },
  progGo: { fontSize: 12, color: '#FA1C51', fontWeight: 600 },
  progSub: { fontSize: 12, color: '#9ca3af' },
  upWrap: { background: '#fff', padding: '10px 16px 12px', borderBottom: '1px solid #f0f0f0' },
  upTitle: { fontSize: 12, color: '#9ca3af', marginBottom: 8 },
  upCard: { borderLeft: '3px solid #FA1C51', paddingLeft: 10, marginBottom: 10 },
  upWhen: { fontSize: 14, fontWeight: 700, color: '#111' },
  upWho: { fontSize: 13, color: '#374151', marginTop: 2 },
  upSub: { fontSize: 12, color: '#9ca3af' },
  upMeta: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  upStore: { color: '#FA1C51', fontWeight: 600 },
  upLink: { fontSize: 12, color: '#FA1C51', textDecoration: 'none' },
  arriveBtn: { marginTop: 8, padding: '7px 16px', background: '#FA1C51', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600 },
  arrivedTag: { marginTop: 8, fontSize: 12, color: '#16a34a' },
  talkBar: { background: '#fff', padding: '0 16px 10px', fontSize: 12, color: '#9ca3af', borderBottom: '1px solid #f0f0f0' },
  msg: { background: '#fff7ed', color: '#9a3412', fontSize: 13, padding: '8px 14px' },
  dealCard: { background: '#fff', border: '1.5px solid #FA1C51', borderRadius: 10, padding: '12px 14px', margin: '10px 0' },
  dealTop: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  dealName: { fontSize: 15, fontWeight: 600, color: '#111' },
  dealSub: { fontSize: 12, color: '#9ca3af' },
  dealTag: { fontSize: 11, fontWeight: 600, color: '#FA1C51', background: '#fff1f4', padding: '1px 7px', borderRadius: 4 },
  dealBio: { fontSize: 12, color: '#6b7280', margin: '6px 0', lineHeight: 1.6 },
  dealLine: { fontSize: 13, color: '#374151', margin: '6px 0 10px' },
  dealActions: { display: 'flex', gap: 8 },
  okBtn: { padding: '7px 16px', background: '#FA1C51', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600 },
  noBtn: { padding: '7px 16px', background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13 },
  verifyRow: { margin: '6px 0' },
  vOk:   { fontSize: 12, color: '#16a34a', background: '#f0fdf4', padding: '2px 8px', borderRadius: 4 },
  vWarn: { fontSize: 12, color: '#b45309', background: '#fffbeb', padding: '2px 8px', borderRadius: 4 },
  vBad:  { fontSize: 12, color: '#dc2626', background: '#fef2f2', padding: '2px 8px', borderRadius: 4 },
  rateCard: { background: '#fff', border: '1.5px solid #16a34a', borderRadius: 10, padding: '12px 14px', margin: '10px 0' },
  starRow: { display: 'flex', gap: 8, marginBottom: 4 },
  starOn: { width: 34, height: 34, lineHeight: '34px', textAlign: 'center', borderRadius: 8, background: '#FA1C51', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  starOff: { width: 34, height: 34, lineHeight: '34px', textAlign: 'center', borderRadius: 8, background: '#f3f4f6', color: '#9ca3af', fontSize: 14, cursor: 'pointer' },
  area: { width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit' },
  settledCard: { background: '#f0fdf4', color: '#15803d', fontSize: 12, borderRadius: 8, padding: '8px 12px', margin: '8px 0' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 60 },
  modal: { background: '#fff', borderRadius: 12, padding: 18, width: '100%', maxWidth: 360, maxHeight: '86vh', overflowY: 'auto' },
  modalTitle: { margin: '0 0 4px', fontSize: 16, fontWeight: 600, color: '#111' },
  modalHint: { margin: '0 0 10px', fontSize: 12, color: '#9ca3af', lineHeight: 1.6 },
  hint: { fontSize: 11, color: '#9ca3af', marginBottom: 4, lineHeight: 1.5 },
  label: { fontSize: 12, color: '#6b7280', margin: '10px 0 5px' },
  input: { width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' },
  slotRow: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 },
  slotSel: { padding: '7px 8px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, background: '#fff' },
  slotIn: { width: 68, padding: '7px 8px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' },
  dash: { fontSize: 12, color: '#9ca3af' },
  del: { color: '#9ca3af', fontSize: 18, padding: '0 4px', cursor: 'pointer' },
  addSlot: { padding: '7px 12px', background: '#fff', color: '#FA1C51', border: '1px dashed #fecdd3', borderRadius: 8, fontSize: 13, marginTop: 4 },
  modalBtns: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 },
  cancel: { padding: '8px 16px', background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13 },
  save: { padding: '8px 16px', background: '#FA1C51', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600 },
};
